import type { BoardRunState, BoardStopResult, RunPot } from '@goodtime/shared';

/**
 * Where a Lamp Run is, from the player's side:
 * - `ready`: waiting for STOP;
 * - `landing`: STOP pressed, the light is slowing onto its cell;
 * - `result`: the landing is showing;
 * - `over`: no stops or no acorns left, waiting for COLLECT (or DONE);
 * - `collecting`: COLLECT pressed;
 * - `done`: collected.
 */
export type BoardPhase = 'idle' | 'ready' | 'landing' | 'result' | 'over' | 'collecting' | 'done';

/** What the SPIN button shows during a run. */
export type SpinMode = 'spin' | 'stop' | 'landing' | 'collect' | 'done';

export interface BoardRunView {
  phase: BoardPhase;
  runId: string | null;
  lucky: boolean;
  stops: number;
  stopsMax: number;
  lives: number;
  livesMax: number;
  pot: RunPot;
  last: BoardStopResult | null;
}

export type BoardEvent =
  | { type: 'start'; run: BoardRunState }
  | { type: 'stopPressed' }
  | { type: 'landed'; result: BoardStopResult }
  | { type: 'stopFailed' }
  | { type: 'resultShown' }
  /** A lit lamp's game, opened by the landing, paid into the run pot. */
  | { type: 'potChanged'; pot: RunPot }
  | { type: 'collectPressed' }
  | { type: 'collected' }
  | { type: 'end' };

/** What the host should do after an event: ask the server to STOP
 *  (with this stop number) or to collect. */
export type BoardEffect = { type: 'requestStop'; stop: number } | { type: 'requestCollect' } | null;

export const IDLE: BoardRunView = {
  phase: 'idle',
  runId: null,
  lucky: false,
  stops: 0,
  stopsMax: 0,
  lives: 0,
  livesMax: 0,
  pot: { coins: 0, gems: 0, freeSpins: 0 },
  last: null,
};

const isOver = (v: Pick<BoardRunView, 'stops' | 'stopsMax' | 'lives'>) => v.stops >= v.stopsMax || v.lives <= 0;

/**
 * The Lamp Run's button logic as a pure reducer. Presses that don't fit the
 * phase do nothing, so mashing STOP (a triple tap) sends one STOP.
 */
export function reduceBoard(view: BoardRunView, event: BoardEvent): { view: BoardRunView; effect: BoardEffect } {
  switch (event.type) {
    case 'start': {
      const r = event.run;
      const next: BoardRunView = {
        phase: 'ready',
        runId: r.runId,
        lucky: r.lucky,
        stops: r.stops,
        stopsMax: r.stopsMax,
        lives: r.lives,
        livesMax: r.livesMax,
        pot: r.pot,
        last: null,
      };
      // A run resumed while a lit lamp's game is open waits on that game,
      // as it does straight after the landing that opened it.
      return { view: { ...next, phase: r.child ? 'result' : isOver(next) ? 'over' : 'ready' }, effect: null };
    }
    case 'stopPressed':
      if (view.phase !== 'ready') return { view, effect: null };
      return { view: { ...view, phase: 'landing' }, effect: { type: 'requestStop', stop: view.stops } };
    case 'landed': {
      if (view.phase !== 'landing') return { view, effect: null };
      const r = event.result;
      return {
        view: { ...view, phase: 'result', stops: r.stops, lives: r.lives, pot: r.pot, last: r },
        effect: null,
      };
    }
    case 'stopFailed':
      // The STOP never reached the server: let the player press it again.
      if (view.phase !== 'landing') return { view, effect: null };
      return { view: { ...view, phase: 'ready' }, effect: null };
    case 'potChanged':
      // Only while the landing that opened the game is showing.
      if (view.phase !== 'result') return { view, effect: null };
      return { view: { ...view, pot: event.pot }, effect: null };
    case 'resultShown':
      if (view.phase !== 'result') return { view, effect: null };
      return { view: { ...view, phase: isOver(view) ? 'over' : 'ready' }, effect: null };
    case 'collectPressed':
      if (view.phase !== 'over') return { view, effect: null };
      return { view: { ...view, phase: 'collecting' }, effect: { type: 'requestCollect' } };
    case 'collected':
      if (view.phase !== 'collecting') return { view, effect: null };
      return { view: { ...view, phase: 'done' }, effect: null };
    case 'end':
      return { view: IDLE, effect: null };
  }
}

/** The SPIN button during a run: STOP while waiting, a dimmed button while
 *  the light lands, then COLLECT (or DONE when the pot is empty). */
export function spinModeFor(view: BoardRunView): SpinMode {
  switch (view.phase) {
    case 'ready':
      return 'stop';
    case 'landing':
    case 'result':
    case 'collecting':
      return 'landing';
    case 'over':
      return potEmpty(view.pot) ? 'done' : 'collect';
    default:
      return 'spin';
  }
}

export function potEmpty(pot: RunPot): boolean {
  return pot.coins === 0 && pot.gems === 0 && pot.freeSpins === 0;
}

/** "STOP n OF m": the next stop while waiting, the one just taken while a
 *  result shows. */
export function stopCounter(view: BoardRunView): string {
  const n = view.phase === 'ready' || view.phase === 'landing' ? view.stops + 1 : view.stops;
  return `STOP ${Math.min(Math.max(n, 1), view.stopsMax)} OF ${view.stopsMax}`;
}
